import { dayjs } from '@goodt-common/utils';

// export const QueryConst = {};

const DateFormat = {
    DATE: 'YYYY-MM-DD',
    TIME: 'HH:mm:ss',
    TIMESTAMP: 'YYYY-MM-DD HH:mm:ss'
};

/**
 * @static
 * @enum {String}
 */
export const QueryKey = {
    /** @member {String} */
    FIELDS: '$fields',
    /** @member {String} */
    FROM: '$from',
    /** @member {String} */
    METRICS: '$metrics',
    /** @member {String} */
    DIMENSIONS: '$dimensions',
    /** @member {String} */
    FILTERS: '$filters',
    /** @member {String} */
    VARIABLES: '$variables',
    /** @member {String} */
    METRICS_CUSTOM: '$metricsCustom',
    /** @member {String} */
    SORT: '$sort',
    /** @member {String} */
    ORDER: '$order'
};

export const QueryKeyRuntime = {
    /** @member {String} */
    DRILLDOWNS: '$drilldowns'
};

/**
 * @static
 * @enum {String}
 */
export const QuerySortDirection = {
    /** @member {String} */
    ASC: 'asc',
    /** @member {String} */
    DESC: 'desc'
};

export const QuerySortDirectionLabel = {
    [QuerySortDirection.ASC]: 'по возрастанию',
    [QuerySortDirection.DESC]: 'по убыванию'
};

/**
 * @static
 * @enum {String}
 */
export const QueryFilterOperator = {
    /**
     * equivalent of sql '='
     * @member {String}
     */
    EQ: '$eq',
    /**
     * equivalent of sql '!='
     * @member {String}
     */
    EQ_NOT: '$eqn',
    /**
     * equivalent of sql 'is'
     * @member {String}
     */
    IS: '$is',
    /**
     * equivalent of sql 'is not'
     * @member {String}
     */
    IS_NOT: '$isn',
    /**
     * equivalent of sql '<'
     * @member {String}
     */
    LESS: '$lt',
    /**
     * equivalent of sql '<='
     * @member {String}
     */
    LESS_EQ: '$ltq',
    /**
     * equivalent of sql '>'
     * @member {String}
     */
    GREATER: '$gt',
    /**
     * equivalent of sql '>='
     * @member {String}
     */
    GREATER_EQ: '$gtq',
    /**
     * equivalent of sql 'in()'
     * @member {String}
     */
    IN: '$in',
    /**
     * equivalent of sql 'not in()'
     * @member {String}
     */
    IN_NOT: '$inn',
    /**
     * equivalent of sql 'between()'
     * @member {String}
     */
    BETWEEN: '$btw',
    /**
     * equivalent of sql 'not between()'
     * @member {String}
     */
    BETWEEN_NOT: '$btwn',
    /**
     * equivalent of sql 'like()'
     * @member {String}
     */
    LIKE: '$lk',
    /**
     * equivalent of sql 'like()' case-insens
     * @member {String}
     */
    LIKE_CI: '$lkci'
};

export const QueryFilterOperatorLabel = {
    [QueryFilterOperator.EQ]: '=',
    [QueryFilterOperator.EQ_NOT]: '!=',
    [QueryFilterOperator.IS]: 'is',
    [QueryFilterOperator.IS_NOT]: 'is not',
    [QueryFilterOperator.LESS]: '<',
    [QueryFilterOperator.LESS_EQ]: '<=',
    [QueryFilterOperator.GREATER]: '>',
    [QueryFilterOperator.GREATER_EQ]: '>=',
    [QueryFilterOperator.IN]: 'in',
    [QueryFilterOperator.IN_NOT]: 'not in',
    [QueryFilterOperator.BETWEEN]: 'between',
    [QueryFilterOperator.BETWEEN_NOT]: 'not between',
    [QueryFilterOperator.LIKE]: 'like',
    [QueryFilterOperator.LIKE_CI]: 'like (ignore)'
};

export const QueryFilterOperatorValidator = {
    [QueryFilterOperator.EQ]: (v) => v.length == 1,
    [QueryFilterOperator.EQ_NOT]: (v) => v.length == 1,
    [QueryFilterOperator.IS]: (v) => v.length == 1,
    [QueryFilterOperator.IS_NOT]: (v) => v.length == 1,
    [QueryFilterOperator.LESS]: (v) => v.length == 1,
    [QueryFilterOperator.LESS_EQ]: (v) => v.length == 1,
    [QueryFilterOperator.GREATER]: (v) => v.length == 1,
    [QueryFilterOperator.GREATER_EQ]: (v) => v.length == 1,
    [QueryFilterOperator.IN]: (v) => v.length > 0,
    [QueryFilterOperator.IN_NOT]: (v) => v.length > 0,
    [QueryFilterOperator.BETWEEN]: (v) => v.length == 2,
    [QueryFilterOperator.BETWEEN_NOT]: (v) => v.length == 2,
    [QueryFilterOperator.LIKE]: (v) => v.length > 0,
    [QueryFilterOperator.LIKE_CI]: (v) => v.length > 0
};

/**
 * @static
 * @enum {String}
 */
export const QueryFilterPlaceholder = {
    /** @member {String} */
    YEAR_ROLLING: '#year-rolling#',
    /** @member {String} */
    YEAR_START: '#year-start#',
    /** @member {String} */
    YEAR_END: '#year-end#',
    /** @member {String} */
    YEAR_PREV_START: '#year-prev-start#',
    /** @member {String} */
    YEAR_PREV_END: '#year-prev-end#',

    /** @member {String} */
    QUARTER_START: '#quarter-start#',
    /** @member {String} */
    QUARTER_END: '#quarter-end#',
    /** @member {String} */
    QUARTER_PY_START: '#quarter-py-start#',
    /** @member {String} */
    QUARTER_PY_END: '#quarter-py-end#',
    /** @member {String} */
    QUARTER_PREV_START: '#quarter-prev-start#',
    /** @member {String} */
    QUARTER_PREV_END: '#quarter-prev-end#',

    /** @member {String} */
    MONTH_ROLLING: '#month-rolling#',
    /** @member {String} */
    MONTH_START: '#month-start#',
    /** @member {String} */
    MONTH_END: '#month-end#',
    /** @member {String} */
    MONTH_PY_START: '#month-py-start#',
    /** @member {String} */
    MONTH_PY_END: '#month-py-end#',
    /** @member {String} */
    MONTH_PREV_START: '#month-prev-start#',
    /** @member {String} */
    MONTH_PREV_END: '#month-prev-end#',

    /** @member {String} */
    WEEK_ROLLING: '#week-rolling#',
    /** @member {String} */
    WEEK_START: '#week-start#',
    /** @member {String} */
    WEEK_END: '#week-end#',
    /** @member {String} */
    WEEK_PY_START: '#week-py-start#',
    /** @member {String} */
    WEEK_PY_END: '#week-py-end#',
    /** @member {String} */
    WEEK_PREV_START: '#week-prev-start#',
    /** @member {String} */
    WEEK_PREV_END: '#week-prev-end#',

    /** @member {String} */
    TODAY: '#today#',
    /** @member {String} */
    YESTERDAY: '#yesterday#'
};

/**
 * @static
 * @enum {String}
 */
export const QueryFilterPlaceholderHandler = {
    /** @member {Function} */
    YEAR_ROLLING: () => dayjs().add(-365, 'd').format(DateFormat.DATE),
    /** @member {Function} */
    YEAR_START: () => dayjs().startOf('year').format(DateFormat.DATE),
    /** @member {Function} */
    YEAR_END: () => dayjs().endOf('year').format(DateFormat.DATE),
    /** @member {Function} */
    YEAR_PREV_START: () => dayjs().add(-1, 'y').startOf('year').format(DateFormat.DATE),
    /** @member {Function} */
    YEAR_PREV_END: () => dayjs().add(-1, 'y').endOf('year').format(DateFormat.DATE),

    /** @member {Function} */
    QUARTER_START: () => dayjs().startOf('Q').format(DateFormat.DATE),
    /** @member {Function} */
    QUARTER_END: () => dayjs().endOf('Q').format(DateFormat.DATE),
    /** @member {Function} */
    QUARTER_PY_START: () => dayjs().add(-1, 'y').startOf('Q').format(DateFormat.DATE),
    /** @member {Function} */
    QUARTER_PY_END: () => dayjs().add(-1, 'y').endOf('Q').format(DateFormat.DATE),
    /** @member {Function} */
    QUARTER_PREV_START: () => dayjs().add(-1, 'Q').startOf('Q').format(DateFormat.DATE),
    /** @member {Function} */
    QUARTER_PREV_END: () => dayjs().add(-1, 'Q').endOf('Q').format(DateFormat.DATE),

    /** @member {Function} */
    MONTH_ROLLING: () => dayjs().add(-30, 'd').format(DateFormat.DATE),
    /** @member {Function} */
    MONTH_START: () => dayjs().startOf('month').format(DateFormat.DATE),
    /** @member {Function} */
    MONTH_END: () => dayjs().endOf('month').format(DateFormat.DATE),
    /** @member {Function} */
    MONTH_PY_START: () => dayjs().add(-1, 'y').startOf('month').format(DateFormat.DATE),
    /** @member {Function} */
    MONTH_PY_END: () => dayjs().add(-1, 'y').endOf('month').format(DateFormat.DATE),
    /** @member {Function} */
    MONTH_PREV_START: () => dayjs().add(-1, 'M').startOf('month').format(DateFormat.DATE),
    /** @member {Function} */
    MONTH_PREV_END: () => dayjs().add(-1, 'M').endOf('month').format(DateFormat.DATE),

    /** @member {Function} */
    WEEK_ROLLING: () => dayjs().add(-7, 'd').format(DateFormat.DATE),
    /** @member {Function} */
    WEEK_START: () => dayjs().startOf('isoWeek').format(DateFormat.DATE),
    /** @member {Function} */
    WEEK_END: () => dayjs().endOf('isoWeek').format(DateFormat.DATE),
    /** @member {Function} */
    WEEK_PY_START: () => dayjs().add(-1, 'y').startOf('isoWeek').format(DateFormat.DATE),
    /** @member {Function} */
    WEEK_PY_END: () => dayjs().add(-1, 'y').endOf('isoWeek').format(DateFormat.DATE),
    /** @member {Function} */
    WEEK_PREV_START: () => dayjs().startOf('isoWeek').add(-1, 'd').startOf('isoWeek').format(DateFormat.DATE),
    /** @member {Function} */
    WEEK_PREV_END: () => dayjs().startOf('isoWeek').add(-1, 'd').endOf('isoWeek').format(DateFormat.DATE),

    /** @member {Function} */
    TODAY: () => dayjs().format(DateFormat.DATE),
    /** @member {Function} */
    YESTERDAY: () => dayjs().add(-1, 'd').format(DateFormat.DATE)
};

/**
 * @static
 * @enum {String}
 */
export const QueryAggregateFunctionName = {
    /**
     * no aggregation
     * @member {String}
     */
    VALUE: 'value',
    /**
     * equivalent of sql 'avg()'
     * @member {String}
     */
    AVG: 'avg',
    /**
     * equivalent of sql 'count()'
     * @member {String}
     */
    COUNT: 'count',
    /**
     * equivalent of sql 'max()'
     * @member {String}
     */
    MAX: 'max',
    /**
     * equivalent of sql 'min()'
     * @member {String}
     */
    MIN: 'min',
    /**
     * equivalent of sql 'sum()'
     * @member {String}
     */
    SUM: 'sum',
    /**
     * equivalent of mysql 'group_concat()'
     * @member {String}
     */
    GROUP_CONCAT: 'group_concat',
    /**
     * equivalent of mysql 'group_concat(distinct)'
     * @member {String}
     */
    GROUP_UCONCAT: 'group_uconcat'
};
